import {
  FEATURE_DEFAULTS,
  type CornerMark,
  type Cutout,
  type FacadeFeature,
  type Placement,
  type PlacementKind,
} from "@/lib/placement";
import type { PlanningDraft } from "@/store/planning";

/**
 * What one step on the board did, in a few words — „Gehrung an Ecke Wand 3
 * zu Wand 4 gesetzt" — for the tooltips on Rückgängig („…: what it takes
 * back") and Wiederholen („…: what it puts back").
 *
 * **Read off the two drafts, not recorded with the gesture.** Rückgängig is
 * the one gesture that does not know what it is about to change (`diffOps`
 * makes the same argument for the ops), and words handed in at seventeen
 * `push` sites would be seventeen chances to say something the step did not
 * do. What the two drafts differ in is what the step did — always.
 *
 * One thing is named, in this order: a corner, a facade object, a hole, the
 * elements. A step that changed several kinds was one gesture about the
 * first of them — a downpipe brings its hole, a filled corner lengthens the
 * grating over it. `null` where nothing differs that a person could name; the
 * tooltip then says just „Rückgängig".
 *
 * `wallCount` is the outline's, so the corner after the last wall is the one
 * to wall 1 — the name its handle on the board carries.
 */
export function describeStep(before: PlanningDraft, after: PlanningDraft, wallCount: number): string | null {
  return (
    describeCorners(before.corners, after.corners, wallCount) ??
    describeFeatures(before.features, after.features) ??
    describeCutouts(before.cutouts, after.cutouts) ??
    describePlacements(before.placements, after.placements)
  );
}

/**
 * Whether two values from a draft say the same — field by field, whatever
 * order the fields were written in, an absent field the same as `undefined`.
 * Not `JSON.stringify`: an object rebuilt with a spread in another order
 * would read as changed and put the wrong thing in the tooltip.
 */
function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((each, at) => same(each, b[at]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  return [...new Set([...Object.keys(left), ...Object.keys(right)])].every((field) => same(left[field], right[field]));
}

/** What changed between two lists keyed by `key`: added, removed, and changed pairs. */
function diff<T extends object>(before: readonly T[], after: readonly T[], key: (each: T) => string | number) {
  const was = new Map(before.map((each) => [key(each), each]));
  const now = new Map(after.map((each) => [key(each), each]));
  return {
    added: after.filter((each) => !was.has(key(each))),
    removed: before.filter((each) => !now.has(key(each))),
    changed: after.flatMap((each) => {
      const old = was.get(key(each));
      return old !== undefined && !same(old, each) ? [{ old, now: each }] : [];
    }),
  };
}

/** Whether `a` and `b` differ in nothing but where they stand. */
function onlyMoved<T extends object>(a: T, b: T, position: readonly (keyof T & string)[]): boolean {
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  return [...new Set([...Object.keys(left), ...Object.keys(right)])].every(
    (field) => (position as readonly string[]).includes(field) || same(left[field], right[field]),
  );
}

function cornerName(wallIndex: number, wallCount: number): string {
  return `Ecke Wand ${wallIndex + 1} zu Wand ${((wallIndex + 1) % Math.max(wallCount, 1)) + 1}`;
}

function describeCorners(before: readonly CornerMark[], after: readonly CornerMark[], wallCount: number): string | null {
  const { added, removed, changed } = diff(before, after, (mark) => mark.wallIndex);
  const count = added.length + removed.length + changed.length;
  if (count === 0) return null;
  if (count > 1) return `${count} Ecken geändert`;
  const set = added[0] ?? changed[0]?.now;
  if (set !== undefined) {
    const name = cornerName(set.wallIndex, wallCount);
    const was = changed[0]?.old;
    if (was !== undefined && was.treatment === set.treatment) return `${name} geändert`;
    return set.treatment === "fuellung" ? `${name} aufgefüllt` : `Gehrung an ${name} gesetzt`;
  }
  const gone = removed[0]!;
  const name = cornerName(gone.wallIndex, wallCount);
  return gone.treatment === "fuellung" ? `Auffüllung an ${name} entfernt` : `Gehrung an ${name} entfernt`;
}

function describeFeatures(before: readonly FacadeFeature[], after: readonly FacadeFeature[]): string | null {
  const { added, removed, changed } = diff(before, after, (feature) => feature.id);
  const count = added.length + removed.length + changed.length;
  if (count === 0) return null;
  if (count > 1) return `${count} Fassadenobjekte geändert`;
  const name = (feature: FacadeFeature) =>
    `${FEATURE_DEFAULTS[feature.featureType]?.name ?? "Objekt"} auf Wand ${feature.wallIndex + 1}`;
  if (added[0]) return `${name(added[0])} gesetzt`;
  if (removed[0]) return `${name(removed[0])} entfernt`;
  const { old, now } = changed[0]!;
  return `${name(now)} ${onlyMoved(old, now, ["wallIndex", "offsetMm"]) ? "verschoben" : "geändert"}`;
}

function describeCutouts(before: readonly Cutout[], after: readonly Cutout[]): string | null {
  const { added, removed, changed } = diff(before, after, (cutout) => cutout.id);
  const count = added.length + removed.length + changed.length;
  if (count === 0) return null;
  if (count > 1) return `${count} Ausschnitte geändert`;
  const name = (cutout: Cutout) => `Ausschnitt auf Wand ${cutout.wallIndex + 1}`;
  if (added[0]) return `${name(added[0])} gesetzt`;
  if (removed[0]) return `${name(removed[0])} entfernt`;
  const { old, now } = changed[0]!;
  return `${name(now)} ${onlyMoved(old, now, ["wallIndex", "offsetMm", "acrossMm", "reference"]) ? "verschoben" : "geändert"}`;
}

/** What the toolbar calls each kind — one and several. */
const KIND_WORDS: Record<PlacementKind, [string, string]> = {
  grating: ["Rost", "Roste"],
  bracket: ["Konsole", "Konsolen"],
  area: ["Außenfläche", "Außenflächen"],
  gap: ["Unterbrechung", "Unterbrechungen"],
};

function describePlacements(before: readonly Placement[], after: readonly Placement[]): string | null {
  const { added, removed, changed } = diff(before, after, (placement) => placement.id);
  const touched = [...added, ...removed, ...changed.map((each) => each.now)];
  if (touched.length === 0) return null;

  if (touched.length === 1) {
    const one = touched[0]!;
    // A grating carries its label on the board („W1-02"); the other kinds
    // are found by their wall.
    const name =
      one.kind === "grating" && one.label ? `Rost ${one.label}` : `${KIND_WORDS[one.kind][0]} auf Wand ${one.wallIndex + 1}`;
    if (added.length === 1) return `${name} gesetzt`;
    if (removed.length === 1) return `${name} entfernt`;
    const { old, now } = changed[0]!;
    return `${name} ${onlyMoved(old, now, ["wallIndex", "offsetMm"]) ? "verschoben" : "geändert"}`;
  }

  const walls = new Set(touched.map((each) => each.wallIndex));
  const wall = walls.size === 1 ? `Wand ${[...walls][0]! + 1}` : null;
  if (added.length > 0 && removed.length === 0 && changed.length === 0) return wall ? `${wall} belegt` : "Wände belegt";
  if (added.length > 0 && removed.length > 0) return wall ? `${wall} neu aufgeteilt` : "Wände neu aufgeteilt";
  const kinds = new Set(touched.map((each) => each.kind));
  const plural = kinds.size === 1 ? KIND_WORDS[touched[0]!.kind][1] : "Elemente";
  const verb = removed.length > 0 && added.length === 0 && changed.length === 0 ? "entfernt" : "geändert";
  return wall ? `${plural} auf ${wall} ${verb}` : `${plural} ${verb}`;
}
